import { useRef, useEffect } from "react";
import { Bold, Italic, List, ListOrdered, Heading2, Heading3, Link2, Quote, Undo, Redo } from "lucide-react";

const ToolBtn = ({ onClick, title, children, testid }) => (
  <button
    type="button"
    title={title}
    data-testid={testid}
    onMouseDown={(e) => { e.preventDefault(); onClick(); }}
    className="flex h-8 w-8 items-center justify-center rounded text-slate-600 transition-colors hover:bg-white hover:text-ukf-red"
  >
    {children}
  </button>
);

export function RichTextEditor({ value, onChange, placeholder = "Write the content…" }) {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || "")) {
      ref.current.innerHTML = value || "";
    }
  }, [value]);

  const exec = (cmd, arg = null) => {
    document.execCommand(cmd, false, arg);
    if (ref.current) onChange(ref.current.innerHTML);
  };

  const addLink = () => {
    const url = window.prompt("Enter URL");
    if (url) exec("createLink", url);
  };

  return (
    <div className="rounded-lg border border-slate-300 bg-white focus-within:border-ukf-red focus-within:ring-2 focus-within:ring-ukf-red/20 transition">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-slate-200 bg-slate-50 p-1.5 rounded-t-lg">
        <ToolBtn onClick={() => exec("bold")} title="Bold" testid="rte-bold"><Bold className="h-4 w-4" /></ToolBtn>
        <ToolBtn onClick={() => exec("italic")} title="Italic" testid="rte-italic"><Italic className="h-4 w-4" /></ToolBtn>
        <span className="mx-1 h-5 w-px bg-slate-200" />
        <ToolBtn onClick={() => exec("formatBlock", "<h2>")} title="Heading" testid="rte-h2"><Heading2 className="h-4 w-4" /></ToolBtn>
        <ToolBtn onClick={() => exec("formatBlock", "<h3>")} title="Subheading" testid="rte-h3"><Heading3 className="h-4 w-4" /></ToolBtn>
        <ToolBtn onClick={() => exec("formatBlock", "<blockquote>")} title="Quote" testid="rte-quote"><Quote className="h-4 w-4" /></ToolBtn>
        <span className="mx-1 h-5 w-px bg-slate-200" />
        <ToolBtn onClick={() => exec("insertUnorderedList")} title="Bullet list" testid="rte-ul"><List className="h-4 w-4" /></ToolBtn>
        <ToolBtn onClick={() => exec("insertOrderedList")} title="Numbered list" testid="rte-ol"><ListOrdered className="h-4 w-4" /></ToolBtn>
        <ToolBtn onClick={addLink} title="Link" testid="rte-link"><Link2 className="h-4 w-4" /></ToolBtn>
        <span className="mx-1 h-5 w-px bg-slate-200" />
        <ToolBtn onClick={() => exec("undo")} title="Undo" testid="rte-undo"><Undo className="h-4 w-4" /></ToolBtn>
        <ToolBtn onClick={() => exec("redo")} title="Redo" testid="rte-redo"><Redo className="h-4 w-4" /></ToolBtn>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        data-testid="rte-editor"
        data-placeholder={placeholder}
        onInput={(e) => onChange(e.currentTarget.innerHTML)}
        className="rte-editor rte-content min-h-[220px] max-w-none px-4 py-3 focus:outline-none"
      />
    </div>
  );
}
